import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { addDays, format, isSameDay, startOfDay } from 'date-fns'
import { es } from 'date-fns/locale'
import clsx from 'clsx'
import { apiErrorMessage } from '../../services/api'
import { Icon, type IconName } from '../../components/ui/icons'
import { ErrorText, Field, Input, Select, Spinner } from '../../components/ui'
import { WHATSAPP_URL } from '../../config/clinic'
import { formatDate, formatTime, MODALITY_LABELS } from '../../utils/format'
import { formatRutInput, isValidPassport, isValidRut } from '../../utils/rut'
import { BackAction, BookingShell, PrimaryAction, ProfessionalAvatar } from './BookingShell'
import {
  confirmByToken,
  fetchAvailability,
  fetchCatalog,
  reserve,
  type BookableService,
  type BookingCatalog,
  type BookingFlow,
  type PublicProfessional,
  type PublicSlot,
  type ReserveResult,
} from './booking.api'

/**
 * Agendamiento en línea en 3 pasos (DT-56). Principio rector: **horas
 * primero, identidad al final** — al revés de RedSalud y Healtheaton, que
 * piden el RUT antes de mostrar una sola hora. Acá la persona ve la oferta,
 * elige, y recién entonces dice quién es.
 */

const TRIAGE_ICONS: Record<string, IconName> = {
  rodilla: 'injury',
  hombro: 'trauma',
  columna: 'posture',
  'evaluacion-deportiva': 'athlete',
  kinesiologia: 'rehab',
  preocupacional: 'preoccupational',
}

const MODALITY_ICONS: Record<string, IconName> = { IN_PERSON: 'location', HOME_VISIT: 'route', TELEMEDICINE: 'video' }

/** Un ícono por flujo y por categoría de examen: el paso 1 se lee de un vistazo. */
const FLOW_ICONS: Record<BookingFlow, IconName> = {
  consultas: 'sportsMedicine',
  examenes: 'preoccupational',
  procedimientos: 'trauma',
}

const CATEGORY_ICONS: Record<string, IconName> = {
  IMAGING: 'imaging',
  LABORATORY: 'lab',
  PREOCCUPATIONAL: 'preoccupational',
  PROCEDURE: 'trauma',
  SURGERY: 'trauma',
}

const EXAM_CATEGORY_LABELS: Record<string, string> = {
  IMAGING: 'Imágenes',
  LABORATORY: 'Laboratorio',
  PREOCCUPATIONAL: 'Salud ocupacional',
  PROCEDURE: 'Procedimientos',
  SURGERY: 'Cirugías',
}

type DayPart = 'all' | 'morning' | 'afternoon'
const DAY_PARTS: Array<{ key: DayPart; label: string; icon: IconName }> = [
  { key: 'all', label: 'Todo el día', icon: 'allDay' },
  { key: 'morning', label: 'Mañana', icon: 'morning' },
  { key: 'afternoon', label: 'Tarde', icon: 'afternoon' },
]

const MAX_WEEKS = 8 // ventana de reserva web: ~60 días

export function BookingPage() {
  const [params] = useSearchParams()
  const [step, setStep] = useState(1)

  // Paso 1
  const [flow, setFlow] = useState<BookingFlow>('consultas')
  const [triage, setTriage] = useState<string | null>(null)
  const [serviceId, setServiceId] = useState('')
  const [professionalId, setProfessionalId] = useState('') // '' = cualquiera
  // Paso 2
  const [slot, setSlot] = useState<PublicSlot | null>(null)
  // Resultado
  const [result, setResult] = useState<ReserveResult | null>(null)

  const catalog = useQuery({ queryKey: ['booking-catalog'], queryFn: fetchCatalog, staleTime: 5 * 60_000 })

  // Entrada desde la landing: ?necesidad=rodilla o ?prestacion=<id>.
  useEffect(() => {
    if (!catalog.data || step !== 1 || serviceId) return
    const need = params.get('necesidad')
    const chip = need ? catalog.data.triage.find((t) => t.key === need) : null
    if (chip?.serviceId) {
      setTriage(chip.key)
      setServiceId(chip.serviceId)
      setStep(2)
      return
    }
    const wanted = params.get('prestacion')
    const service = wanted ? catalog.data.services.find((s) => s.id === wanted) : null
    if (service) {
      setFlow(service.flow)
      setServiceId(service.id)
      setStep(2)
    }
  }, [catalog.data, params, step, serviceId])

  const service = catalog.data?.services.find((s) => s.id === serviceId) ?? null

  /** Elegir motivo o examen salta directo a las horas: es un clic, no un formulario. */
  function chooseService(sid: string, triageKey: string | null = null) {
    setTriage(triageKey)
    setServiceId(sid)
    setProfessionalId('')
    setSlot(null)
    setStep(2)
    window.scrollTo({ top: 0 })
  }

  if (result) return <Confirmation result={result} onAnother={() => window.location.assign('/agendar')} />

  if (step === 1) {
    return (
      <BookingShell
        step={1}
        action={
          service ? (
            <>
              <span className="hidden text-sm text-steel-600 sm:inline">{service.name}</span>
              <PrimaryAction onClick={() => setStep(2)}>
                Ver horas disponibles
                <Icon name="arrowRight" className="h-4 w-4" />
              </PrimaryAction>
            </>
          ) : undefined
        }
      >
        <StepNeed
          catalog={catalog.data}
          loading={catalog.isLoading}
          error={catalog.isError ? apiErrorMessage(catalog.error) : ''}
          flow={flow}
          onFlow={(f) => {
            setFlow(f)
            setTriage(null)
            setServiceId('')
            setProfessionalId('')
            setSlot(null)
          }}
          serviceId={serviceId}
          professionalId={professionalId}
          triage={triage}
          onChoose={chooseService}
          onService={(id) => {
            setTriage(null)
            setServiceId(id)
            setProfessionalId('')
            setSlot(null)
          }}
          onProfessional={(id) => {
            setProfessionalId(id)
            setSlot(null)
          }}
        />
      </BookingShell>
    )
  }

  if (step === 2 && service) {
    return (
      <BookingShell
        step={2}
        action={
          <>
            <BackAction onClick={() => setStep(1)} />
            <PrimaryAction disabled={!slot} onClick={() => setStep(3)}>
              {slot ? `Continuar con las ${formatTime(slot.startsAt)}` : 'Elige una hora'}
              <Icon name="arrowRight" className="h-4 w-4" />
            </PrimaryAction>
          </>
        }
      >
        <StepSlots
          service={service}
          professionals={(catalog.data?.professionals ?? []).filter((p) => service.professionalIds.includes(p.id))}
          professionalId={professionalId}
          onProfessional={(id) => {
            setProfessionalId(id)
            setSlot(null)
          }}
          slot={slot}
          onSlot={setSlot}
          locations={catalog.data?.locations ?? []}
        />
      </BookingShell>
    )
  }

  if (step === 3 && service && slot) {
    const professional = catalog.data?.professionals.find((p) => p.id === slot.professionalId)
    const location = catalog.data?.locations.find((l) => l.id === slot.locationId)
    return (
      <StepIdentity
        service={service}
        slot={slot}
        professional={professional}
        locationName={location?.name ?? ''}
        insuranceProviders={catalog.data?.insuranceProviders ?? []}
        triage={triage}
        onBack={() => setStep(2)}
        onDone={setResult}
      />
    )
  }

  // Estado inconsistente (p. ej. recarga a mitad de camino): se vuelve al inicio.
  return (
    <BookingShell step={1}>
      <Spinner />
    </BookingShell>
  )
}

// ---------- Paso 1: ¿Qué necesitas? ----------

function StepNeed({
  catalog,
  loading,
  error,
  flow,
  onFlow,
  serviceId,
  professionalId,
  triage,
  onChoose,
  onService,
  onProfessional,
}: {
  catalog?: BookingCatalog
  loading: boolean
  error: string
  flow: BookingFlow
  onFlow: (flow: BookingFlow) => void
  serviceId: string
  professionalId: string
  triage: string | null
  onChoose: (serviceId: string, triageKey?: string | null) => void
  onService: (id: string) => void
  onProfessional: (id: string) => void
}) {
  const service = catalog?.services.find((s) => s.id === serviceId)
  const eligible = (catalog?.professionals ?? []).filter((p) => service?.professionalIds.includes(p.id))
  // Solo se ofrecen los flujos que tienen algo que reservar hoy: una pestaña
  // vacía es una promesa falsa (hoy los procedimientos con abono no entran).
  const flows = (catalog?.flows ?? []).filter((f) => catalog?.services.some((s) => s.flow === f.key))
  const inFlow = (catalog?.services ?? []).filter((s) => s.flow === flow)

  return (
    <div className="space-y-6">
      <div className="flex items-end gap-3">
        {/* El personaje "apuntando" (docs/BRAND.md) señala las opciones —
            también en móvil, más chico: es la primera pantalla del flujo. */}
        <img
          src="/brand/personaje/apuntando.webp"
          alt=""
          width={232}
          height={500}
          decoding="async"
          className="h-24 w-auto select-none md:h-40"
        />
        <div className="pb-1">
          <h1 className="font-display text-2xl font-bold text-ink md:text-3xl">¿Qué necesitas?</h1>
          <p className="mt-1 text-sm text-steel-600">Elige una opción y te mostramos las horas disponibles. Sin registrarte.</p>
        </div>
      </div>

      <ErrorText>{error}</ErrorText>
      {loading && <Spinner />}

      {catalog && (
        <>
          {/* Consultas y exámenes son dos flujos distintos: la hora de una
              consulta la da el profesional, la de un examen la sala. */}
          {flows.length > 1 && (
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Tipo de atención">
              {flows.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  aria-selected={flow === f.key}
                  onClick={() => onFlow(f.key)}
                  className={clsx(
                    'inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors',
                    flow === f.key
                      ? 'border-brand-600 bg-brand-600 text-white'
                      : 'border-steel-200 bg-white text-steel-700 hover:border-brand-300',
                  )}
                >
                  <Icon name={FLOW_ICONS[f.key]} className="h-4 w-4" />
                  {f.label}
                </button>
              ))}
            </div>
          )}
          <p className="-mt-3 text-sm text-steel-600">{flows.find((f) => f.key === flow)?.hint}</p>

          {flow === 'consultas' ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" role="group" aria-label="Motivo de consulta">
                {catalog.triage.map((chip) => {
                  const enabled = Boolean(chip.serviceId)
                  const active = triage === chip.key
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      disabled={!enabled}
                      aria-pressed={active}
                      onClick={() => chip.serviceId && onChoose(chip.serviceId, chip.key)}
                      className={clsx(
                        'group rounded-2xl border bg-white p-4 text-left shadow-sm transition-all',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                        active
                          ? 'border-brand-500 ring-2 ring-brand-200'
                          : 'border-steel-100 hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md',
                        !enabled && 'cursor-not-allowed opacity-50',
                      )}
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-soft text-brand-600 transition-colors group-hover:bg-brand-100">
                        <Icon name={TRIAGE_ICONS[chip.key] ?? 'sportsMedicine'} className="h-5 w-5" />
                      </span>
                      <span className="mt-3 block font-display font-semibold text-ink">{chip.label}</span>
                      <span className="mt-0.5 block text-xs text-steel-600">{chip.hint}</span>
                    </button>
                  )
                })}
              </div>

              <div className="rounded-2xl border border-steel-100 bg-white p-4 shadow-sm">
                <p className="font-display font-semibold text-ink">O elige directamente</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <Field label="Prestación">
                    <Select value={triage ? '' : serviceId} onChange={(e) => onService(e.target.value)}>
                      <option value="">Selecciona…</option>
                      {inFlow.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} · {s.durationMin} min
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Profesional" hint={service ? undefined : 'Primero elige la prestación'}>
                    <Select value={professionalId} onChange={(e) => onProfessional(e.target.value)} disabled={!service}>
                      <option value="">Cualquiera — la primera hora libre</option>
                      {eligible.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.firstName} {p.lastName}
                          {p.specialty ? ` — ${p.specialty}` : ''}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
                {service && (
                  <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-soft px-3 py-1 text-xs font-medium text-brand-700">
                    <Icon name={MODALITY_ICONS[service.modality]} className="h-3.5 w-3.5" />
                    {MODALITY_LABELS[service.modality]} · {service.durationMin} min
                  </p>
                )}
              </div>
            </>
          ) : (
            <ExamPicker services={inFlow} onChoose={onChoose} />
          )}

          <p className="text-xs text-steel-500">
            ¿Cirugías o procedimientos con abono? Necesitan evaluación previa: escríbenos por{' '}
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-brand-700 hover:underline">
              WhatsApp
            </a>
            .
          </p>
        </>
      )}
    </div>
  )
}

/**
 * Exámenes y procedimientos, agrupados por categoría y con su código Fonasa
 * cuando la clínica ya lo cargó: es el dato con el que la gente compara y con
 * el que su isapre bonifica.
 */
function ExamPicker({ services, onChoose }: { services: BookableService[]; onChoose: (serviceId: string) => void }) {
  const categories = [...new Set(services.map((s) => s.category))]

  if (services.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-steel-200 bg-white p-6 text-center text-sm text-steel-600">
        Todavía no hay exámenes con reserva en línea. Escríbenos por WhatsApp y te agendamos.
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {categories.map((category) => (
        <div key={category}>
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-steel-500">
            <Icon name={CATEGORY_ICONS[category] ?? 'sportsMedicine'} className="h-3.5 w-3.5" />
            {EXAM_CATEGORY_LABELS[category] ?? category}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {services
              .filter((s) => s.category === category)
              .map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onChoose(s.id)}
                  className="group flex items-start gap-3 rounded-2xl border border-steel-100 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-soft text-brand-600 transition-colors group-hover:bg-brand-100">
                    <Icon name={CATEGORY_ICONS[s.category] ?? 'sportsMedicine'} className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-display font-semibold text-ink">{s.name}</span>
                    <span className="mt-0.5 block text-xs text-steel-600">
                      {s.durationMin} min
                      {s.fonasaCode ? ` · Código Fonasa ${s.fonasaCode}` : ''}
                    </span>
                    {s.requiresMedicalOrder && (
                      <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                        <Icon name="document" className="h-3 w-3" />
                        Trae tu orden médica
                      </span>
                    )}
                  </span>
                </button>
              ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ---------- Paso 2: Elige tu hora ----------

function StepSlots({
  service,
  professionals,
  professionalId,
  onProfessional,
  slot,
  onSlot,
  locations,
}: {
  service: BookableService
  professionals: PublicProfessional[]
  professionalId: string
  onProfessional: (id: string) => void
  slot: PublicSlot | null
  onSlot: (s: PublicSlot) => void
  locations: BookingCatalog['locations']
}) {
  const [weekOffset, setWeekOffset] = useState(0)
  const [part, setPart] = useState<DayPart>('all')
  const [day, setDay] = useState<Date | null>(null)

  const from = useMemo(() => addDays(startOfDay(new Date()), weekOffset * 7), [weekOffset])
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(from, i)), [from])

  const slots = useQuery({
    queryKey: ['booking-availability', service.id, professionalId, from.toISOString()],
    queryFn: () => fetchAvailability(service.id, professionalId || undefined, from, 7),
  })

  const visible = useMemo(
    () =>
      (slots.data ?? []).filter((s) => {
        const h = new Date(s.startsAt).getHours()
        return part === 'all' || (part === 'morning' ? h < 13 : h >= 13)
      }),
    [slots.data, part],
  )
  const byDay = (d: Date) => visible.filter((s) => isSameDay(new Date(s.startsAt), d))

  // Día seleccionado en móvil: el primero con horas, si el actual no tiene.
  useEffect(() => {
    if (!slots.data) return
    if (day && byDay(day).length > 0) return
    const first = days.find((d) => byDay(d).length > 0) ?? null
    setDay(first)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots.data, part, weekOffset])

  const nameOf = (id: string) => {
    const p = professionals.find((x) => x.id === id)
    return p ? `${p.firstName} ${p.lastName}` : ''
  }
  const chosenProfessional = professionals.find((p) => p.id === professionalId)

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink md:text-3xl">Elige tu hora</h1>
        <p className="mt-1 text-sm text-steel-600">
          {service.name} · {service.durationMin} min · {MODALITY_LABELS[service.modality]}
          {service.fonasaCode ? ` · Código Fonasa ${service.fonasaCode}` : ''}
        </p>
        {service.schedulingMode === 'RESOURCE' && (
          <p className="mt-2 flex items-start gap-1.5 text-sm text-steel-600">
            <Icon name="location" className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
            Estas son las horas de la sala donde se toma el examen, no la agenda de un médico.
            {service.requiresMedicalOrder ? ' Recuerda traer tu orden médica.' : ''}
          </p>
        )}
      </div>

      {/* Profesional: cualquiera (primera hora libre) o uno en particular. */}
      {professionals.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Profesional">
          <button
            type="button"
            aria-pressed={!professionalId}
            onClick={() => onProfessional('')}
            className={clsx(
              'shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
              !professionalId ? 'border-brand-600 bg-brand-600 text-white' : 'border-steel-200 bg-white text-steel-700 hover:border-brand-300',
            )}
          >
            Cualquier profesional
          </button>
          {professionals.map((p) => (
            <button
              key={p.id}
              type="button"
              aria-pressed={professionalId === p.id}
              onClick={() => onProfessional(p.id)}
              className={clsx(
                'flex shrink-0 items-center gap-2 rounded-full border py-1 pl-1 pr-3.5 text-sm font-medium transition-colors',
                professionalId === p.id ? 'border-brand-600 bg-brand-600 text-white' : 'border-steel-200 bg-white text-steel-700 hover:border-brand-300',
              )}
            >
              <ProfessionalAvatar firstName={p.firstName} lastName={p.lastName} className="h-7 w-7 text-xs" />
              {p.firstName} {p.lastName}
            </button>
          ))}
        </div>
      )}
      {(chosenProfessional ?? (professionals.length === 1 ? professionals[0] : null)) && (
        <ProfessionalCard professional={chosenProfessional ?? professionals[0]} />
      )}

      {/* Semana + filtro mañana/tarde */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setWeekOffset((w) => Math.max(0, w - 1))}
            disabled={weekOffset === 0}
            aria-label="Semana anterior"
            className="rounded-full border border-steel-200 bg-white p-2 text-steel-700 hover:border-brand-300 disabled:opacity-40"
          >
            <Icon name="prev" className="h-4 w-4" />
          </button>
          <p className="min-w-36 text-center text-sm font-medium text-ink">
            {format(days[0], "d 'de' MMM", { locale: es })} – {format(days[6], "d 'de' MMM", { locale: es })}
          </p>
          <button
            type="button"
            onClick={() => setWeekOffset((w) => Math.min(MAX_WEEKS, w + 1))}
            disabled={weekOffset >= MAX_WEEKS}
            aria-label="Semana siguiente"
            className="rounded-full border border-steel-200 bg-white p-2 text-steel-700 hover:border-brand-300 disabled:opacity-40"
          >
            <Icon name="next" className="h-4 w-4" />
          </button>
        </div>
        <div className="flex rounded-full border border-steel-200 bg-white p-0.5" role="group" aria-label="Horario">
          {DAY_PARTS.map((dp) => (
            <button
              key={dp.key}
              type="button"
              aria-pressed={part === dp.key}
              onClick={() => setPart(dp.key)}
              className={clsx(
                'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors',
                part === dp.key ? 'bg-brand-600 text-white' : 'text-steel-600 hover:text-brand-700',
              )}
            >
              <Icon name={dp.icon} className="h-3.5 w-3.5" />
              {dp.label}
            </button>
          ))}
        </div>
      </div>

      {visible.length > 0 && visible.every((s) => s.isOverbooking) && (
        <p className="flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <Icon name="clock" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Las horas de esta semana están tomadas. Lo que ves son <strong>cupos de sobrecupo</strong>: se atienden
            entre dos horas ya agendadas, así que la espera puede ser mayor.
          </span>
        </p>
      )}

      {slots.isLoading ? (
        <Spinner />
      ) : slots.isError ? (
        <ErrorText>{apiErrorMessage(slots.error)}</ErrorText>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-steel-200 bg-white p-6 text-center text-sm text-steel-600">
          Sin horas libres en esta semana{part !== 'all' ? ' para ese horario' : ''}.
          <br />
          Prueba la semana siguiente{professionalId ? ', otro profesional' : ''} o escríbenos por WhatsApp.
        </div>
      ) : (
        <>
          {/* Móvil: tira de días + horas del día elegido. */}
          <div className="md:hidden">
            <div className="flex gap-2 overflow-x-auto pb-2" role="tablist" aria-label="Día">
              {days.map((d) => {
                const n = byDay(d).length
                const active = day ? isSameDay(day, d) : false
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    disabled={n === 0}
                    onClick={() => setDay(d)}
                    className={clsx(
                      'flex w-16 shrink-0 flex-col items-center rounded-2xl border py-2 text-xs transition-colors',
                      active ? 'border-brand-600 bg-brand-600 text-white' : 'border-steel-200 bg-white text-steel-700',
                      n === 0 && 'opacity-40',
                    )}
                  >
                    <span className="uppercase">{format(d, 'EEE', { locale: es })}</span>
                    <span className="font-display text-lg font-bold">{format(d, 'd')}</span>
                    <span className={active ? 'text-brand-100' : 'text-steel-500'}>{n} {n === 1 ? 'hora' : 'horas'}</span>
                  </button>
                )
              })}
            </div>
            {day && (
              <SlotList
                slots={byDay(day)}
                selected={slot}
                onSelect={onSlot}
                showProfessional={!professionalId && professionals.length > 1}
                nameOf={nameOf}
              />
            )}
          </div>

          {/* Escritorio: las 7 columnas de la semana. */}
          <div className="hidden grid-cols-7 gap-2 md:grid">
            {days.map((d) => {
              const list = byDay(d)
              return (
                <div key={d.toISOString()} className="min-w-0">
                  <p className={clsx('mb-2 text-center text-xs font-semibold uppercase', list.length ? 'text-ink' : 'text-steel-400')}>
                    {format(d, 'EEE d', { locale: es })}
                  </p>
                  {list.length === 0 ? (
                    <p className="text-center text-xs text-steel-300">—</p>
                  ) : (
                    <SlotList
                      slots={list}
                      selected={slot}
                      onSelect={onSlot}
                      showProfessional={!professionalId && professionals.length > 1}
                      nameOf={nameOf}
                      compact
                    />
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}

      {slot && (
        <p className="rounded-xl bg-soft px-4 py-3 text-sm text-ink">
          <Icon name="scheduleCheck" className="mr-1.5 inline h-4 w-4 text-brand-600" />
          <strong>{formatDate(slot.startsAt)}</strong> a las <strong>{formatTime(slot.startsAt)}</strong>
          {nameOf(slot.professionalId) ? ` con ${nameOf(slot.professionalId)}` : ''} ·{' '}
          {locations.find((l) => l.id === slot.locationId)?.name}
          {slot.isOverbooking ? ' · sobrecupo' : ''}
        </p>
      )}
    </div>
  )
}

function ProfessionalCard({ professional }: { professional: PublicProfessional }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-steel-100 bg-white p-3">
      <ProfessionalAvatar firstName={professional.firstName} lastName={professional.lastName} className="h-12 w-12" />
      <div className="min-w-0">
        <p className="font-display font-semibold text-ink">
          {professional.firstName} {professional.lastName}
        </p>
        {professional.specialty && <p className="truncate text-sm text-steel-600">{professional.specialty}</p>}
      </div>
    </div>
  )
}

function SlotList({
  slots,
  selected,
  onSelect,
  showProfessional,
  nameOf,
  compact,
}: {
  slots: PublicSlot[]
  selected: PublicSlot | null
  onSelect: (s: PublicSlot) => void
  showProfessional: boolean
  nameOf: (id: string) => string
  compact?: boolean
}) {
  return (
    <div className={clsx(compact ? 'flex flex-col gap-1.5' : 'grid grid-cols-3 gap-2 sm:grid-cols-4')}>
      {slots.map((s) => {
        const active = selected?.startsAt === s.startsAt && selected.professionalId === s.professionalId
        const name = showProfessional ? nameOf(s.professionalId) : ''
        return (
          <button
            key={`${s.startsAt}-${s.professionalId}`}
            type="button"
            aria-pressed={active}
            onClick={() => onSelect(s)}
            title={s.isOverbooking ? 'Cupo de sobrecupo: se atiende entre dos horas ya agendadas' : name || undefined}
            className={clsx(
              'rounded-xl border py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              active
                ? 'border-brand-600 bg-brand-600 text-white'
                : s.isOverbooking
                  ? 'border-dashed border-amber-400 bg-white text-amber-800 hover:bg-amber-50'
                  : 'border-steel-200 bg-white text-brand-700 hover:border-brand-400 hover:bg-soft',
            )}
          >
            {formatTime(s.startsAt)}
            {s.isOverbooking && (
              <span className={clsx('block text-[10px] font-medium uppercase', active ? 'text-brand-100' : 'text-amber-700')}>
                sobrecupo
              </span>
            )}
            {name && !compact && !s.isOverbooking && (
              <span className={clsx('block truncate px-1 text-[10px] font-normal', active ? 'text-brand-100' : 'text-steel-500')}>
                {name.split(' ')[0]}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ---------- Paso 3: Identifícate y confirma ----------

function StepIdentity({
  service,
  slot,
  professional,
  locationName,
  insuranceProviders,
  triage,
  onBack,
  onDone,
}: {
  service: BookableService
  slot: PublicSlot
  professional?: PublicProfessional
  locationName: string
  insuranceProviders: BookingCatalog['insuranceProviders']
  triage: string | null
  onBack: () => void
  onDone: (r: ReserveResult) => void
}) {
  const [documentType, setDocumentType] = useState<'RUT' | 'PASSPORT'>('RUT')
  const [form, setForm] = useState({
    document: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    insuranceProviderId: '',
    visitAddress: '',
    consent: false,
  })
  const [error, setError] = useState('')

  const documentOk = documentType === 'RUT' ? isValidRut(form.document) : isValidPassport(form.document)
  const documentTouched = form.document.length > 0
  const ready =
    documentOk &&
    form.firstName.trim().length >= 2 &&
    form.lastName.trim().length >= 2 &&
    form.email.includes('@') &&
    form.phone.trim().length >= 8 &&
    Boolean(form.insuranceProviderId) &&
    form.consent &&
    (service.modality !== 'HOME_VISIT' || form.visitAddress.trim().length > 5)

  const submit = useMutation({
    mutationFn: () =>
      reserve({
        serviceId: service.id,
        professionalId: slot.professionalId,
        startsAt: slot.startsAt,
        documentType,
        document: form.document,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        insuranceProviderId: form.insuranceProviderId || null,
        triage,
        visitAddress: service.modality === 'HOME_VISIT' ? form.visitAddress : null,
        consent: true,
      }),
    onSuccess: (r) => {
      onDone(r)
      window.scrollTo({ top: 0 })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    if (ready) submit.mutate()
  }

  return (
    <BookingShell
      step={3}
      action={
        <>
          <BackAction onClick={onBack} />
          <PrimaryAction type="submit" form="reserva" disabled={!ready || submit.isPending}>
            {submit.isPending ? 'Reservando…' : 'Confirmar reserva'}
            {!submit.isPending && <Icon name="check" className="h-4 w-4" />}
          </PrimaryAction>
        </>
      }
    >
      <div className="space-y-5">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink md:text-3xl">Identifícate y confirma</h1>
          <p className="mt-1 text-sm text-steel-600">Es lo último. Con esto te mandamos la confirmación y el enlace para gestionar tu hora.</p>
        </div>

        <Summary service={service} startsAt={slot.startsAt} professional={professional} locationName={locationName} />

        <form id="reserva" onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-steel-100 bg-white p-4 shadow-sm">
          <div className="flex rounded-full border border-steel-200 bg-surface p-0.5" role="group" aria-label="Tipo de documento">
            {(['RUT', 'PASSPORT'] as const).map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={documentType === t}
                onClick={() => {
                  setDocumentType(t)
                  setForm({ ...form, document: '' })
                }}
                className={clsx(
                  'flex-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
                  documentType === t ? 'bg-brand-600 text-white' : 'text-steel-600',
                )}
              >
                {t === 'RUT' ? 'RUT' : 'Pasaporte'}
              </button>
            ))}
          </div>

          <Field
            label={documentType === 'RUT' ? 'RUT' : 'Número de pasaporte'}
            hint={documentTouched && !documentOk ? (documentType === 'RUT' ? 'Revisa el RUT: el dígito verificador no coincide' : 'Entre 5 y 12 letras o números') : undefined}
          >
            <Input
              value={form.document}
              inputMode={documentType === 'RUT' ? 'numeric' : 'text'}
              autoComplete="off"
              placeholder={documentType === 'RUT' ? '12.345.678-5' : 'AB123456'}
              onChange={(e) => setForm({ ...form, document: documentType === 'RUT' ? formatRutInput(e.target.value) : e.target.value.toUpperCase() })}
              aria-invalid={documentTouched && !documentOk}
              className={clsx(documentTouched && (documentOk ? 'border-emerald-400' : 'border-red-400'))}
              required
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nombres">
              <Input value={form.firstName} autoComplete="given-name" onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
            </Field>
            <Field label="Apellidos">
              <Input value={form.lastName} autoComplete="family-name" onChange={(e) => setForm({ ...form, lastName: e.target.value })} required />
            </Field>
            <Field label="Correo" hint="Ahí llega la confirmación con el enlace para confirmar o anular.">
              <Input type="email" value={form.email} autoComplete="email" onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </Field>
            <Field label="Teléfono (WhatsApp)">
              <Input type="tel" value={form.phone} autoComplete="tel" placeholder="+56 9 …" onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
            </Field>
            {/* Previsión desde el catálogo de la clínica (DT-60): el mercado
                cambia de isapres sin avisarnos. */}
            <Field label="Previsión">
              <Select value={form.insuranceProviderId} onChange={(e) => setForm({ ...form, insuranceProviderId: e.target.value })}>
                <option value="">Selecciona…</option>
                {insuranceProviders.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </Field>
            {service.modality === 'HOME_VISIT' && (
              <Field label="Dirección de la visita" hint="Calle, número y comuna.">
                <Input value={form.visitAddress} autoComplete="street-address" onChange={(e) => setForm({ ...form, visitAddress: e.target.value })} required />
              </Field>
            )}
          </div>

          <label className="flex items-start gap-2.5 rounded-xl bg-soft p-3 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-steel-300 text-brand-600 focus:ring-brand-500"
              required
            />
            <span>
              Autorizo a Cima Sport a tratar mis datos para agendar y gestionar mi atención, según la{' '}
              <strong>Ley 21.719</strong>. Puedo revocarlo y pedir su eliminación escribiendo a la clínica.
            </span>
          </label>

          <ErrorText>{error}</ErrorText>
          <p className="flex items-center gap-1.5 text-xs text-steel-500">
            <Icon name="lock" className="h-3.5 w-3.5" />
            Si ya eres paciente, la confirmación va al correo que tenemos registrado.
          </p>
        </form>
      </div>
    </BookingShell>
  )
}

function Summary({
  service,
  startsAt,
  professional,
  locationName,
}: {
  service: { name: string; durationMin: number; modality: string }
  startsAt: string
  professional?: { firstName: string; lastName: string; specialty?: string | null }
  locationName: string
}) {
  return (
    <div className="rounded-2xl bg-brand-900 p-4 text-white">
      <p className="text-xs font-semibold uppercase tracking-widest text-brand-300">Tu hora</p>
      <p className="mt-1 font-display text-xl font-bold">
        {formatDate(startsAt)} · {formatTime(startsAt)} h
      </p>
      <p className="mt-1 text-sm text-brand-100">
        {service.name} · {service.durationMin} min · {MODALITY_LABELS[service.modality]}
      </p>
      {professional && (
        <p className="mt-2 flex items-center gap-2 text-sm">
          <ProfessionalAvatar firstName={professional.firstName} lastName={professional.lastName} className="h-8 w-8 text-xs" />
          {professional.firstName} {professional.lastName}
          {professional.specialty ? <span className="text-brand-200"> · {professional.specialty}</span> : null}
        </p>
      )}
      {locationName && (
        <p className="mt-1 flex items-center gap-1.5 text-sm text-brand-100">
          <Icon name="location" className="h-4 w-4" />
          {locationName}
        </p>
      )}
    </div>
  )
}

// ---------- Confirmación ----------

function Confirmation({ result, onAnother }: { result: ReserveResult; onAnother: () => void }) {
  const [status, setStatus] = useState(result.appointment.status)
  const [error, setError] = useState('')
  const confirm = useMutation({
    mutationFn: () => confirmByToken(result.token),
    onSuccess: (view) => setStatus(view.status),
    onError: (err) => setError(apiErrorMessage(err)),
  })
  const a = result.appointment

  return (
    <BookingShell step={4}>
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <Icon name="circleCheck" className="h-7 w-7" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-bold text-ink md:text-3xl">
              {result.alreadyExisted ? 'Esta hora ya estaba reservada' : `¡Listo, ${result.patientFirstName}!`}
            </h1>
            <p className="mt-1 text-sm text-steel-600">
              {status === 'CONFIRMED' ? 'Tu hora quedó confirmada.' : 'Tu hora quedó reservada.'}
              {result.sentTo && (
                <>
                  {' '}
                  Te enviamos la confirmación a <strong>{result.sentTo}</strong> con un enlace para confirmar, anular o ver tu cita — sin
                  claves ni registro.
                </>
              )}
            </p>
          </div>
        </div>

        <Summary service={{ ...a.service, modality: a.modality }} startsAt={a.startsAt} professional={a.professional} locationName={a.location.name} />

        <ErrorText>{error}</ErrorText>
        <div className="flex flex-col gap-2 sm:flex-row">
          {status !== 'CONFIRMED' ? (
            <button
              type="button"
              onClick={() => confirm.mutate()}
              disabled={confirm.isPending}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-600 px-6 py-3 font-display text-sm font-semibold text-white hover:bg-brand-700 disabled:bg-brand-300"
            >
              <Icon name="scheduleCheck" className="h-4 w-4" />
              {confirm.isPending ? 'Confirmando…' : 'Confirmar asistencia ahora'}
            </button>
          ) : (
            <span className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-50 px-6 py-3 font-display text-sm font-semibold text-emerald-700">
              <Icon name="check" className="h-4 w-4" />
              Asistencia confirmada
            </span>
          )}
          <Link
            to={`/cita/${result.token}`}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-300 bg-white px-6 py-3 font-display text-sm font-semibold text-brand-700 hover:bg-soft"
          >
            <Icon name="view" className="h-4 w-4" />
            Ver o gestionar mi cita
          </Link>
        </div>

        <div className="rounded-2xl border border-steel-100 bg-white p-4 text-sm text-steel-700">
          <p className="font-display font-semibold text-ink">Qué sigue</p>
          <ul className="mt-2 space-y-1.5">
            <li className="flex gap-2"><Icon name="mailCheck" className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />Te recordaremos la hora un día antes.</li>
            <li className="flex gap-2"><Icon name="clock" className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />Si no puedes venir, anula desde el enlace con anticipación y liberas el cupo para otra persona.</li>
            <li className="flex gap-2"><Icon name="idCard" className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />Trae tu cédula y, si tienes, exámenes o imágenes previas.</li>
            {/* El paciente tiene derecho a saber que su hora es un sobrecupo. */}
            {a.isOverbooking && (
              <li className="flex gap-2">
                <Icon name="clock" className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                Tu hora es un <strong>cupo de sobrecupo</strong>: se atiende entre dos horas ya agendadas, así que la
                espera puede ser mayor de lo habitual.
              </li>
            )}
          </ul>
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          <button type="button" onClick={onAnother} className="font-medium text-brand-700 hover:underline">
            Reservar otra hora
          </button>
          <Link to="/" className="font-medium text-steel-600 hover:underline">
            Volver al sitio
          </Link>
        </div>
      </div>
    </BookingShell>
  )
}
